export * from './map-response';
export * from './to-iso-string';

import { get } from 'lodash-es';

import {
  BosErrorResponse,
  CheckBasketResponse,
  CheckOutResponse,
  CityPassCloseOrderResponse,
  CloseOrderResponse,
  OrderResponse,
  ResponseCode,
  ResponseCodes,
  Shift4ErrorResponse,
  Shift4SaleResponse,
  TokenizedCardResponse,
  ValidateCouponCodeResponse,
  CardError,
  ERROR_KEY,
} from '../types/response';
import {
  ChampagneToastTimeConstraints,
  DateConstraints,
  SantaTimeConstraints,
  StatGroup,
  TimeConstraints,
} from '../constants';

import type { BOSProduct } from '../types';
import type { I4GoChdErrorResponse, I4GoChdResponse } from '../../shift4/types';

export const isShift4Error = (
  response:
    | void
    | TokenizedCardResponse
    | Shift4SaleResponse
    | Shift4ErrorResponse,
): response is Shift4ErrorResponse => {
  return (
    response instanceof Object &&
    (response as Shift4ErrorResponse).result &&
    (response as Shift4ErrorResponse).result[0] &&
    (response as Shift4ErrorResponse).result[0].error !== undefined
  );
};

export const isI4GoError = (
  response: I4GoChdResponse | I4GoChdErrorResponse,
): response is I4GoChdErrorResponse => {
  return (
    response instanceof Object &&
    (response as I4GoChdErrorResponse).i4go_response === 'FAILURE'
  );
};

export const isBosError = (
  response:
    | BosErrorResponse
    | CheckBasketResponse
    | CheckOutResponse
    | OrderResponse
    | CityPassCloseOrderResponse
    | CloseOrderResponse<OrderResponse | CityPassCloseOrderResponse>
    | ValidateCouponCodeResponse,
): response is BosErrorResponse => {
  return (
    (response as BosErrorResponse).ERROR !== undefined &&
    (response as BosErrorResponse).ERROR.CODE !== '200'
  );
};

export const isBosProduct = (response: BOSProduct): response is BOSProduct => {
  return (response as BOSProduct).PRODUCTTYPE !== undefined;
};

export const transactionResponseHandler = (
  response: Shift4SaleResponse | Shift4ErrorResponse,
) => {
  // Generic response code check
  const code: ResponseCode = get(
    response,
    'result[0].transaction.responseCode' as ResponseCode,
    'A' as ResponseCode,
  );

  if (['A', 'C'].indexOf(code) === -1) {
    // Check immediately for `.error` property
    if (isShift4Error(response)) {
      const { longText, primaryCode, shortText } = response.result[0].error;
      return {
        isSuccess: false,
        longText,
        primaryCode,
        shortText,
        ...ResponseCodes['e'],
      };
    }

    // Check response code. There might not be an `.error` property, but there
    // can be a response code that indicates an error.
    const avsCode = response.result[0].transaction.avs?.result;
    const scCode = response.result[0].card.securityCode.result;

    // Trip the declined response if AVS or Security Code is invalid
    if (avsCode && avsCode !== 'Y') {
      const text = CardError.AVS[avsCode as ERROR_KEY];

      return {
        isSuccess: false,
        longText: text,
        primaryCode: avsCode,
        shortText: 'AVS Failure',
      };
    }

    if (scCode && scCode !== 'M') {
      const text = CardError.CVV[scCode as ERROR_KEY];

      return {
        isSuccess: false,
        longText: text,
        primaryCode: scCode,
        shortText: 'CVV Failure',
      };
    }
  }

  // If we reach this point, the response code is valid
  if (['A', 'C'].indexOf(code) !== -1) {
    return {
      isSuccess: true,
      longText: 'Success',
      primaryCode: code,
      shortText: '200',
      ...ResponseCodes[code],
    };
  }

  // If we reach this point, the transaction is declined by default
  const { type, text } = ResponseCodes[code];
  const declinedResponse = {
    isSuccess: false,
    longText: text,
    primaryCode: code,
    shortText: type,
    ...ResponseCodes[code],
  };
  return declinedResponse;
};

export const isWithinTimeConstraints = (
  statGroup: string,
  hours: number,
  minutes: string,
) => {
  if (statGroup in TimeConstraints) {
    const timeConstraint =
      TimeConstraints[statGroup as keyof typeof TimeConstraints];
    if (timeConstraint) {
      const { startTime, endTime } = timeConstraint;

      // Convert all times to minutes for easier comparison
      const currentTimeInMinutes = hours * 60 + parseInt(minutes);
      const startTimeInMinutes =
        startTime.hours * 60 + parseInt(startTime.minutes);
      const endTimeInMinutes = endTime.hours * 60 + parseInt(endTime.minutes);

      // Check if the current time is within the constraints
      if (
        currentTimeInMinutes >= startTimeInMinutes &&
        currentTimeInMinutes <= endTimeInMinutes
      ) {
        return true;
      } else {
        return false;
      }
    }
  }
  // Defaulting to true if the stagGroup used is not within the TimeConstraints constant
  return true;
};

export const isWithinDateConstraints = (statGroup: StatGroup, date: string) => {
  const constraints = DateConstraints[statGroup];
  if (constraints && constraints.indexOf(date) !== -1) {
    return true;
  }
  return false;
};

export function getChampagneToastTimeConstraint(date: Date) {
  // getDay() returns 0 for Sunday, 1-6 for Monday-Saturday
  return date.getDay() === 0
    ? ChampagneToastTimeConstraints.sunday
    : ChampagneToastTimeConstraints.weekday;
}

export const isWithinChampagneToastTimeConstraint = (
  date: Date,
  hours: number,
  minutes: string,
) => {
  const timeConstraint = getChampagneToastTimeConstraint(date);
  const { startTime, endTime } = timeConstraint;

  // Convert all times to minutes for easier comparison
  const currentTimeInMinutes = hours * 60 + parseInt(minutes);
  const startTimeInMinutes = startTime.hours * 60 + parseInt(startTime.minutes);
  const endTimeInMinutes = endTime.hours * 60 + parseInt(endTime.minutes);

  // Check if the current time is within the constraints
  if (
    currentTimeInMinutes >= startTimeInMinutes &&
    currentTimeInMinutes <= endTimeInMinutes
  ) {
    return true;
  } else {
    return false;
  }
};

export const isWithinSantaTimeConstraints = (
  date: string, // expected format: '12-06'
  hours: number,
  minutes: string,
) => {
  if (date in SantaTimeConstraints) {
    const timeConstraint = SantaTimeConstraints[date];

    if (timeConstraint) {
      const { startTime, endTime } = timeConstraint;

      // Convert all times to minutes for easier comparison
      const currentTimeInMinutes = hours * 60 + parseInt(minutes);
      const startTimeInMinutes =
        startTime.hours * 60 + parseInt(startTime.minutes);
      const endTimeInMinutes = endTime.hours * 60 + parseInt(endTime.minutes);

      // Check if the current time is within the constraints
      if (
        currentTimeInMinutes >= startTimeInMinutes &&
        currentTimeInMinutes <= endTimeInMinutes
      ) {
        // console.log(
        //   `Returning true: Time ${hours}:${minutes} is within constraints for date ${date}`,
        // );
        return true;
      } else {
        // console.log(
        //   `Returning false: Time ${hours}:${minutes} is outside constraints for date ${date}`,
        // );
        return false;
      }
    }
  }
  // Defaulting to false if there is no date with time constraints
  // console.log(`Returning false: No time constraints found for date ${date}`);
  return false;
};

export const getStatGroupKeyByValue = (
  object: { [key in StatGroup]?: string },
  value: string,
): StatGroup | undefined => {
  for (const key in object) {
    if (object[key as keyof typeof object] === value) {
      return key as StatGroup;
    }
  }
  return undefined;
};
